"use client";

import Image from "next/image";
import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Menu, Search, User, X } from "lucide-react";
import { EJINSIGHT_SEARCH_ROUTE, ROUTES } from "@/lib/routes";
import { EjInsightSearchBar } from "./search/search-bar";
import { Link, usePathname } from "@/i18n/routing";
import { EJINSIGHT_COLORS, EJINSIGHT_FONT, EJINSIGHT_TYPE } from "@/lib/theme/ejinsight";
import { navLinks as NAV_LINKS } from "@/fixtures/ejinsight/fixtures";

export function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const pathname = usePathname();

  /**
   * A section is active when you are ON it.
   *
   * "Active" used to be a fixed `isHome` flag in the fixture, so Home was
   * coloured on every page of the site and no other section was ever marked —
   * including while you were reading it. `usePathname` from `@/i18n/routing`
   * returns the path WITHOUT the locale prefix, which is the shape the fixture
   * hrefs are written in, so this is an equality test: an article does not
   * light up its section, and the home page lights up Home alone.
   */
  const isActive = (href: string) => pathname === href;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const headerStyle = useMemo<React.CSSProperties>(
    () => ({
      backgroundColor: scrolled
        ? "rgba(255,255,255,0.86)"
        : EJINSIGHT_COLORS.surface,
      backdropFilter: scrolled ? "saturate(160%) blur(12px)" : undefined,
      WebkitBackdropFilter: scrolled ? "saturate(160%) blur(12px)" : undefined,
      boxShadow: scrolled
        ? "0 1px 0 rgba(19,52,58,0.08), 0 8px 24px -16px rgba(19,52,58,0.18)"
        : `inset 0 -1px 0 ${EJINSIGHT_COLORS.hairline}`,
      transition: "background-color .25s ease, box-shadow .25s ease",
      fontFamily: EJINSIGHT_FONT.body,
      color: EJINSIGHT_COLORS.ink,
    }),
    [scrolled],
  );

  return (
    <header className="sticky top-0 z-50 w-full" style={headerStyle}>
      {/*
        Back to HKEJ.

        This masthead had no way out of the vertical — a reader could reach EJ
        Insight from the main site and never find the way back, which is the
        defect QA filed against EJ Tech ("missing back to HKEJ navigation on
        navbar"). Same utility strip, in EJ Insight's own surface and rule.

        A PLAIN anchor to an absolute `/zh-TW`: it leaves the client router
        entirely, and the destination is the main site, which is Traditional
        Chinese — so an English reader of this vertical lands somewhere that
        exists. `flex h-full` makes the whole strip beside the mark clickable
        rather than only the logo's own box.

        The strip adds 33px to the header, so `--hkej-sticky-nav` in this
        vertical's layout moves with it — otherwise every pinned rail slides
        underneath the masthead.
      */}
      <div
        style={{
          backgroundColor: EJINSIGHT_COLORS.surfaceAlt,
          borderBottom: `1px solid ${EJINSIGHT_COLORS.hairline}`,
        }}
      >
        <div className="mx-auto w-full max-w-[1280px] px-4 sm:px-6 lg:px-8">
          <div className="flex h-8 items-center">
            <a
              href="/zh-TW"
              aria-label="Back to Hong Kong Economic Journal"
              className="group flex h-full w-fit items-center gap-1.5 pr-2 text-[color:var(--rest-fg)] transition-colors hover:text-[color:var(--hover-fg)] motion-reduce:transition-none"
              style={
                {
                  ["--rest-fg" as string]: EJINSIGHT_COLORS.muted,
                  ["--hover-fg" as string]: EJINSIGHT_COLORS.primary,
                } as React.CSSProperties
              }
            >
              <ArrowLeft className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-x-0.5 motion-reduce:transition-none" />
              <Image
                src="/images/nav-v2/hkej-back.png"
                alt="hkej.com"
                width={133}
                height={23}
                className="h-[16px] w-auto object-contain"
              />
            </a>
          </div>
        </div>
      </div>

      <div className="mx-auto flex w-full max-w-[1280px] items-center gap-6 px-4 py-3 sm:px-6 lg:gap-8 lg:px-8 lg:py-4">
        {/* Mobile hamburger */}
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          aria-label="Open menu"
          className="-ml-2 inline-flex h-10 w-10 items-center justify-center lg:hidden"
        >
          <Menu className="h-6 w-6" style={{ color: EJINSIGHT_COLORS.ink }} />
        </button>

        {/* Logo */}
        <Link
          href="/ejinsight"
          aria-label="EJ Insight"
          className="inline-flex shrink-0 items-center"
        >
          <Image
            src="/images/ejinsight/eji-logo.png"
            alt="EJ Insight"
            width={170}
            height={48}
            className="h-9 w-auto sm:h-10"
            priority
            unoptimized
          />
        </Link>

        {/* Desktop nav */}
        <nav
          aria-label="Primary"
          className="hidden flex-1 lg:flex lg:justify-center"
        >
          <ul className="flex items-center gap-1">
            {NAV_LINKS.map((link) => (
              <li key={link.label}>
                <Link
                  href={link.href}
                  aria-current={isActive(link.href) ? "page" : undefined}
                  className="group relative inline-flex items-center px-3 py-2 transition-colors"
                  style={{
                    ...EJINSIGHT_TYPE.navLink,
                    color: isActive(link.href)
                      ? EJINSIGHT_COLORS.primary
                      : EJINSIGHT_COLORS.ink,
                  }}
                >
                  <span>{link.label}</span>
                  <span
                    aria-hidden
                    className={`absolute inset-x-3 -bottom-[3px] h-[3px] origin-left scale-x-0 rounded-full transition-transform duration-300 group-hover:scale-x-100 ${
                      isActive(link.href) ? "scale-x-100" : ""
                    }`}
                    style={{ backgroundColor: EJINSIGHT_COLORS.primary }}
                  />
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* Right actions */}
        <div className="ml-auto flex items-center gap-2 lg:ml-0 lg:gap-3">
          {/* Bare <button>s with no handler: both icons looked live and did
              nothing. A control that advertises itself and does nothing is the
              defect QA filed against lj's copy; these are the same components
              in the other verticals. */}
          {/* The vertical's OWN search, not the parent paper's — QA filed this
              on EJ Tech and EJ Health as "missing search page". */}
          <Link
            href={EJINSIGHT_SEARCH_ROUTE}
            aria-label="Search"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full transition-colors hover:bg-black/5"
          >
            <Search className="h-5 w-5" style={{ color: EJINSIGHT_COLORS.ink }} />
          </Link>
          <Link
            href={ROUTES.profile}
            aria-label="Account"
            className="hidden h-10 w-10 items-center justify-center rounded-full transition-colors hover:bg-black/5 sm:inline-flex"
          >
            <User className="h-5 w-5" style={{ color: EJINSIGHT_COLORS.ink }} />
          </Link>
          {/* Was a `<button>` with no handler. */}
          <Link
            href={ROUTES.subscription}
            className="group ml-1 inline-flex items-center rounded-full px-4 py-2 transition-all duration-200"
            style={{
              ...EJINSIGHT_TYPE.navLink,
              fontSize: 13,
              fontWeight: 700,
              color: EJINSIGHT_COLORS.textOnDark,
              backgroundColor: EJINSIGHT_COLORS.primary,
              boxShadow: `0 0 0 0 ${EJINSIGHT_COLORS.primary}`,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = `0 0 0 3px ${EJINSIGHT_COLORS.primarySoft}`;
              e.currentTarget.style.backgroundColor =
                EJINSIGHT_COLORS.primaryDark;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = `0 0 0 0 ${EJINSIGHT_COLORS.primary}`;
              e.currentTarget.style.backgroundColor = EJINSIGHT_COLORS.primary;
            }}
          >
            Subscribe
          </Link>
        </div>
      </div>

      {/* Mobile drawer */}
      {/* Kept MOUNTED so it can animate both ways.

          It was `{mobileOpen && …}`, which unmounts the panel the moment it
          closes — so it appeared and vanished instantly and there was nothing
          to transition. QA: "add a smooth slide in and out transition on the
          sidenav". The panel translates off its own left edge and the scrim
          fades; `pointer-events-none` while closed keeps the invisible layer
          from swallowing clicks on the page underneath, and `aria-hidden`
          keeps it out of the accessibility tree. */}
      <div
        className={`fixed inset-0 z-[60] lg:hidden ${
          mobileOpen ? "" : "pointer-events-none"
        }`}
        aria-hidden={!mobileOpen}
      >
          <button
            type="button"
            tabIndex={mobileOpen ? 0 : -1}
            aria-label="Close menu"
            className={`absolute inset-0 bg-black/50 transition-opacity duration-300 ease-out motion-reduce:transition-none ${
              mobileOpen ? "opacity-100" : "opacity-0"
            }`}
            onClick={() => setMobileOpen(false)}
          />
          <div
            // Opens from the LEFT, beside the hamburger that summons it — QA:
            // "the sidenav should be on the left side". It slid in from the
            // right while its trigger sat on the far left of the masthead.
            className={`absolute left-0 top-0 flex h-full w-[88%] max-w-[380px] flex-col overflow-y-auto p-6 transition-transform duration-300 ease-out motion-reduce:transition-none ${
              mobileOpen ? "translate-x-0" : "-translate-x-full"
            }`}
            style={{
              backgroundColor: EJINSIGHT_COLORS.surface,
              fontFamily: EJINSIGHT_FONT.body,
              color: EJINSIGHT_COLORS.ink,
            }}
          >
            <div className="mb-6 flex items-center justify-between">
              <Image
                src="/images/ejinsight/eji-logo.png"
                alt="EJ Insight"
                width={140}
                height={40}
                className="h-8 w-auto"
                unoptimized
              />
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                aria-label="Close"
                className="inline-flex h-10 w-10 items-center justify-center rounded-full"
                style={{ border: `1px solid ${EJINSIGHT_COLORS.hairline}` }}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* No search field here. QA: "remove the search bar". The
                masthead's search icon is visible at every width and opens the
                vertical's own search page, so the drawer's copy was a second
                entry point that only ever looked like one. */}

            <ul className="mb-8 flex flex-col">
              {NAV_LINKS.map((link) => (
                <li key={link.label}>
                  <Link
                    href={link.href}
                    onClick={() => setMobileOpen(false)}
                    // Same rule as the desktop row: marked only on its route.
                    aria-current={isActive(link.href) ? "page" : undefined}
                    className="block py-3 transition-colors"
                    style={{
                      fontFamily: EJINSIGHT_FONT.display,
                      fontSize: 22,
                      fontWeight: 600,
                      color: isActive(link.href)
                        ? EJINSIGHT_COLORS.primary
                        : EJINSIGHT_COLORS.ink,
                      borderBottom: `1px solid ${EJINSIGHT_COLORS.hairlineSoft}`,
                    }}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>

            <div className="mt-auto flex flex-col gap-5 pt-6">
              {/* Account, not a locale switcher. QA: "remove the locale
                  switcher; add account button". The switcher duplicated the one
                  in the masthead, which is visible at every width; the account
                  link was the one control the drawer had no route to. */}
              <Link
                href={ROUTES.profile}
                onClick={() => setMobileOpen(false)}
                className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-3"
                style={{
                  ...EJINSIGHT_TYPE.navLink,
                  fontSize: 14,
                  fontWeight: 700,
                  color: EJINSIGHT_COLORS.ink,
                  border: `1px solid ${EJINSIGHT_COLORS.hairline}`,
                }}
              >
                <User className="h-4 w-4" />
                Account
              </Link>
              <Link
                href={ROUTES.subscription}
                onClick={() => setMobileOpen(false)}
                className="inline-flex items-center justify-center rounded-full px-5 py-3"
                style={{
                  ...EJINSIGHT_TYPE.navLink,
                  fontSize: 14,
                  fontWeight: 700,
                  color: EJINSIGHT_COLORS.textOnDark,
                  backgroundColor: EJINSIGHT_COLORS.primary,
                }}
              >
                Subscribe
              </Link>
            </div>
          </div>
      </div>
    </header>
  );
}
